/**
 * React Aria treats `selectedKey={undefined}` as uncontrolled. A click can
 * paint the option label while form state stays empty, so a required field
 * still fails validation ("is a required field") and the label looks selected.
 * `null` keeps the select controlled with nothing selected.
 *
 * Numeric oneOf values (DBS workforce codes) must stay numbers. A string key
 * does not match a numeric option id, and the integer schema rejects it.
 */
export function controlledSelectKey(data: unknown): string | number | null {
  if (typeof data === "number" && Number.isFinite(data)) {
    return data;
  }
  if (typeof data === "string" && data !== "") {
    return data;
  }
  return null;
}

/**
 * React Aria may report a numeric option id as a string. Store the option's
 * own value so integer choices stay integers and string choices stay strings.
 * Exact matches win. A string key is mapped to a number only when that number
 * is actually an option, so string dropdowns are left alone.
 */
export function selectValueForKey(
  key: string | number,
  optionValues: readonly (string | number)[]
): string | number {
  const exact = optionValues.find((value) => value === key);
  if (exact !== undefined) {
    return exact;
  }
  const numericOption = optionValues.find(
    (value) => typeof value === "number" && String(value) === String(key)
  );
  return numericOption !== undefined ? numericOption : key;
}

/**
 * Check-results forms add optional objects (for example education) with no
 * stored value. Required oneOf fields that declare a schema default, such as
 * date awarded resolution, should start as that default once the field is
 * editable so Save can persist them. Summary/read-only view must not write
 * the default, or the form looks saved and later server refreshes are ignored.
 * Returns the default to write, or undefined when the current value should
 * be left alone.
 */
export function defaultSelectValueToCommit(args: {
  data: unknown;
  required: boolean | undefined;
  isEditable: boolean | undefined;
  schemaDefault: unknown;
  optionValues: string[];
}): string | undefined {
  const { data, required, isEditable, schemaDefault, optionValues } = args;
  if (!isEditable || !required) {
    return undefined;
  }
  if (data !== undefined && data !== null && data !== "") {
    return undefined;
  }
  if (typeof schemaDefault !== "string" || schemaDefault === "") {
    return undefined;
  }
  if (!optionValues.includes(schemaDefault)) {
    return undefined;
  }
  return schemaDefault;
}
